export const WEBSITE_SECTION_TYPES = [
  'HERO',
  'ABOUT',
  'PLANS',
  'CLASSES',
  'TRAINERS',
  'FACILITIES',
  'GALLERY',
  'TESTIMONIALS',
  'FAQ',
  'CONTACT',
  'MAP',
  'CTA',
  'RICH_TEXT',
  'MENU',
  'PAGE_HEADER',
  'MEDIA_TEXT',
  'COLUMNS',
  'STATS',
  'TIMELINE',
  'STEPS',
  'QUOTE',
  'IMAGE',
  'VIDEO',
  'LOGOS',
  'SCHEDULE',
  'OFFER',
  'LOCATIONS',
  'HOURS',
  'SOCIAL',
  'BUTTONS',
  'SPACER',
  'DIVIDER',
  'EMBED',
  'RECOGNITION',
  'CAREERS',
] as const;
export type WebsiteSectionType = (typeof WEBSITE_SECTION_TYPES)[number];

export const WEBSITE_TEMPLATES = ['classic', 'bold'] as const;
export type WebsiteTemplate = (typeof WEBSITE_TEMPLATES)[number];

export const WEBSITE_BLOCK_CATEGORIES = ['intro', 'content', 'media', 'gym', 'action', 'layout', 'advanced'] as const;
export type WebsiteBlockCategory = (typeof WEBSITE_BLOCK_CATEGORIES)[number];

export const WEBSITE_BLOCK_CATEGORY: Record<WebsiteSectionType, WebsiteBlockCategory> = {
  HERO: 'intro',
  PAGE_HEADER: 'intro',
  RICH_TEXT: 'content',
  ABOUT: 'content',
  MEDIA_TEXT: 'content',
  COLUMNS: 'content',
  FACILITIES: 'content',
  STATS: 'content',
  TIMELINE: 'content',
  STEPS: 'content',
  QUOTE: 'content',
  FAQ: 'content',
  TESTIMONIALS: 'content',
  IMAGE: 'media',
  GALLERY: 'media',
  VIDEO: 'media',
  LOGOS: 'media',
  PLANS: 'gym',
  CLASSES: 'gym',
  SCHEDULE: 'gym',
  TRAINERS: 'gym',
  RECOGNITION: 'gym',
  MENU: 'gym',
  OFFER: 'gym',
  LOCATIONS: 'gym',
  HOURS: 'gym',
  CONTACT: 'gym',
  MAP: 'gym',
  CAREERS: 'gym',
  SOCIAL: 'gym',
  CTA: 'action',
  BUTTONS: 'action',
  SPACER: 'layout',
  DIVIDER: 'layout',
  EMBED: 'advanced',
};

/** The looks each block can take. The first one is what `default` means for that block. */
export const WEBSITE_BLOCK_VARIANTS: Record<WebsiteSectionType, readonly string[]> = {
  HERO: ['cover', 'centered', 'split', 'minimal'],
  PAGE_HEADER: ['plain', 'image'],
  RICH_TEXT: ['default'],
  ABOUT: ['image-right', 'image-left', 'stacked'],
  MEDIA_TEXT: ['image-left', 'image-right'],
  COLUMNS: ['cards', 'plain', 'icons'],
  FACILITIES: ['cards', 'list'],
  STATS: ['row', 'cards'],
  TIMELINE: ['vertical', 'alternating'],
  STEPS: ['numbered', 'cards'],
  QUOTE: ['large', 'card'],
  FAQ: ['default'],
  TESTIMONIALS: ['grid', 'carousel'],
  IMAGE: ['contained', 'full'],
  GALLERY: ['grid', 'masonry', 'carousel'],
  VIDEO: ['contained', 'full'],
  LOGOS: ['row', 'grid'],
  PLANS: ['cards', 'table'],
  CLASSES: ['grid', 'list'],
  SCHEDULE: ['week', 'list'],
  TRAINERS: ['grid', 'carousel', 'compact'],
  RECOGNITION: ['cards', 'list'],
  MENU: ['default'],
  OFFER: ['banner', 'card'],
  LOCATIONS: ['cards', 'list'],
  HOURS: ['table', 'card'],
  CONTACT: ['default'],
  MAP: ['default'],
  SOCIAL: ['row', 'headline'],
  CTA: ['banner', 'split', 'minimal'],
  BUTTONS: ['default'],
  SPACER: ['default'],
  DIVIDER: ['default'],
  EMBED: ['default'],
  CAREERS: ['list', 'cards'],
};

export const WEBSITE_SCHEMES = ['default', 'muted', 'inverse', 'brand', 'dark', 'light'] as const;
export type WebsiteScheme = (typeof WEBSITE_SCHEMES)[number];

export const WEBSITE_SPACINGS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
export const WEBSITE_WIDTHS = ['narrow', 'normal', 'wide', 'full'] as const;
export const WEBSITE_ALIGNS = ['start', 'center'] as const;
export const WEBSITE_DEVICES = ['mobile', 'desktop'] as const;

export const WEBSITE_ICONS = [
  'dumbbell',
  'heart',
  'activity',
  'pool',
  'sauna',
  'spa',
  'shower',
  'parking',
  'wifi',
  'locker',
  'cafe',
  'food',
  'shop',
  'kids',
  'bike',
  'run',
  'yoga',
  'music',
  'tv',
  'clock',
  'timer',
  'trophy',
  'users',
  'shield',
  'sparkles',
  'sun',
  'snow',
  'water',
  'zap',
  'star',
  'check',
  'calendar',
  'map',
  'phone',
  'mail',
] as const;
export type WebsiteIcon = (typeof WEBSITE_ICONS)[number];
